import { useEffect, useState } from 'react';
import { postingLogApi } from '@/services/postingLogApi';
import type { PostingLogRow } from '@/utils/PostingLog/types';
import { mapPostingLogRow } from '@/utils/PostingLog/mappers';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { getMockPostingLogData } from '@/data/mockPostingLogData';

export function usePostingLogLines(timesheetId: string | null, open: boolean, refreshKey: number) {
  const { isDemo } = useScreenDataMode('reports-posting-log');
  const [lines, setLines] = useState<PostingLogRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!timesheetId || !open) return;

    setLoadError(null);

    if (isDemo) {
      const response = getMockPostingLogData({ timesheetId: Number(timesheetId), sortBy: 'transactionDate', sortDir: 'asc' });
      setLines(response.items.map(mapPostingLogRow));
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLines([]);
    postingLogApi
      .getAllPostingLogRows({ timesheetId: Number(timesheetId), sortBy: 'transactionDate', sortDir: 'asc' })
      .then((items) => { if (!cancelled) setLines(items.map(mapPostingLogRow)); })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLines([]);
          setLoadError(err instanceof Error ? err.message : 'Failed to load lines');
        }
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [timesheetId, open, refreshKey, isDemo]);

  return { lines, isLoading, loadError };
}
